import {Component} from '@theme/component';

/**
 * @typedef {object} WaterSavingsCalculatorRefs
 * @property {HTMLElement} stateIntro
 * @property {HTMLElement} stateStep1
 * @property {HTMLElement} stateStep2
 * @property {HTMLElement} stateResults
 * @property {HTMLElement} barResults
 * @property {HTMLElement} tapResults
 * @property {HTMLElement} heroSaveLabel
 * @property {HTMLElement} heroAmount
 * @property {HTMLElement} heroUnit
 * @property {HTMLElement} heroSubtitle
 * @property {HTMLElement} heroAnnual
 * @property {HTMLElement} heroIconBottled
 * @property {HTMLElement} heroIconPitcher
 * @property {HTMLElement} heroIconTap
 * @property {HTMLElement} payTitle
 * @property {HTMLElement} payBoroux
 * @property {HTMLElement} barBoroux
 * @property {HTMLElement} payPitcher
 * @property {HTMLElement} barPitcher
 * @property {HTMLElement} rowPitcher
 * @property {HTMLElement} payBottled
 * @property {HTMLElement} barBottled
 * @property {HTMLElement} rowBottled
 * @property {HTMLElement} payTakeaway
 * @property {HTMLElement} contaminantTakeaway
 * @property {HTMLElement} rowPitcherC
 * @property {HTMLElement} rowBottledC
 * @property {HTMLElement} impactBottlesWrap
 * @property {HTMLElement} impactBottles
 * @property {HTMLElement} impactBottlesLabel
 * @property {HTMLElement} impactPayoff
 * @property {HTMLElement} impactPayoffLabel
 * @property {HTMLElement} impactDaily
 * @property {HTMLElement} impactDailyLabel
 * @property {HTMLElement} savingsFooter
 * @property {HTMLElement} footnote
 * @property {HTMLElement} tapBorouxPrice
 * @property {HTMLElement} tapBorouxUnit
 * @property {HTMLElement} tapDailyOutput
 * @property {HTMLElement} tapImpactDaily
 * @property {HTMLElement} tapImpactAnnual
 * @property {HTMLElement} tapImpactAnnualLabel
 * @property {HTMLElement} tapFooterMsg
 * @property {HTMLElement} tapFooterNote
 * @property {HTMLElement} configData
 * @property {HTMLElement[]} unitButtons
 * @property {HTMLElement[]} sourceOptions
 * @property {HTMLElement[]} householdOptions
 * @extends {Component<WaterSavingsCalculatorRefs>}
 */
export class WaterSavingsCalculator extends Component {
  requiredRefs = ['stateIntro', 'stateStep1', 'stateStep2', 'stateResults'];

  #source = null;
  #people = null;
  #unit = 'day';
  #config = {};
  #startedFired = false;
  #completedFired = false;

  connectedCallback() {
    super.connectedCallback();
    this.#parseConfig();
  }

  #parseConfig() {
    const script = this.refs.configData;
    if (!script) return;
    try {
      this.#config = JSON.parse(script.textContent);
    } catch {
      console.error('WaterSavingsCalculator: failed to parse config');
    }
  }

  startQuiz() {
    this.refs.stateIntro.hidden = true;
    this.refs.stateStep1.hidden = false;

    // GTM dependency: container GTM-NKQ3CHSH, tag "GA4 - Calculator Started" listens for this event name.
    if (!this.#startedFired) {
      this.#startedFired = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'calculator_started' });
    }
  }

  selectSource(data) {
    this.#source = data.source;
    this.#setActiveOption(this.refs.sourceOptions, data.source);

    setTimeout(() => {
      this.refs.stateStep1.hidden = true;
      this.refs.stateStep2.hidden = false;
    }, 250);
  }

  selectHousehold(data) {
    this.#people = parseInt(data.size, 10);
    this.#setActiveOption(this.refs.householdOptions, data.size);

    setTimeout(() => {
      this.refs.stateStep2.hidden = true;
      this.refs.stateResults.hidden = false;
      this.#showResults();

      // GTM dependency: container GTM-NKQ3CHSH, tag "GA4 - Calculator Completed" listens for this event name.
      if (!this.#completedFired) {
        this.#completedFired = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'calculator_completed' });
      }
    }, 250);
  }

  toggleUnit(data) {
    this.#unit = data.unit;
    for (const btn of this.refs.unitButtons) {
      btn.setAttribute('aria-pressed', btn.dataset.unit === data.unit ? 'true' : 'false');
    }
    this.#showResults();
  }

  restart() {
    this.#source = null;
    this.#people = null;
    this.#unit = 'day';

    this.refs.stateResults.hidden = true;
    this.refs.stateStep2.hidden = true;
    this.refs.stateStep1.hidden = true;
    this.refs.stateIntro.hidden = false;

    for (const btn of this.refs.unitButtons) {
      btn.setAttribute('aria-pressed', btn.dataset.unit === 'day' ? 'true' : 'false');
    }
    for (const opt of this.refs.sourceOptions) opt.classList.remove('wsc__option--active');
    for (const opt of this.refs.householdOptions) opt.classList.remove('wsc__option--active');
  }

  #setActiveOption(options, value) {
    for (const opt of options) {
      opt.classList.toggle('wsc__option--active', opt.dataset.value === String(value));
    }
  }

  #showResults() {
    if (!this.#source || !this.#people) return;

    this.#updateHeroIcon();
    this.#updateHeroSubtitle();

    this.refs.heroSaveLabel.hidden = this.#source === 'tap';

    if (this.#source === 'tap') {
      this.refs.barResults.hidden = true;
      this.refs.tapResults.hidden = false;
      this.#updateTapResults();
    } else {
      this.refs.barResults.hidden = false;
      this.refs.tapResults.hidden = true;
      this.#updateBarResults();
    }
  }

  #updateHeroIcon() {
    if (this.refs.heroIconBottled) this.refs.heroIconBottled.hidden = this.#source !== 'bottled';
    if (this.refs.heroIconPitcher) this.refs.heroIconPitcher.hidden = this.#source !== 'pitcher';
    if (this.refs.heroIconTap) this.refs.heroIconTap.hidden = this.#source !== 'tap';
  }

  #updateHeroSubtitle() {
    const sourceNames = {bottled: 'Bottled Water', pitcher: 'Pitcher Filter', tap: 'Tap Water'};
    const householdLabel = this.#people <= 2 ? '1-2 people' : this.#people <= 4 ? '3-4 people' : '5+ people';
    const sourceName = sourceNames[this.#source] || this.#source;
    if (this.#source === 'tap') {
      this.refs.heroSubtitle.textContent = 'Your cost for clean, filtered water with BOROUX compared to Tap Water for ' + householdLabel;
    } else {
      this.refs.heroSubtitle.textContent = 'Your savings with BOROUX compared to ' + sourceName + ' for ' + householdLabel;
    }
  }

  #updateBarResults() {
    const c = this.#config;
    const people = this.#people;
    const unit = this.#unit;
    const dailyGal = people;
    const annualGal = dailyGal * 365;

    const bxDaily = c.borouxAnnual / 365;
    const pitDaily = (annualGal / c.pitcherGalPerFilter * c.pitcherPerFilter) / 365;
    const botDaily = dailyGal * c.bottledPerGal;

    const bxGal = c.borouxAnnual / annualGal;
    const pitGal = c.pitcherPerFilter / c.pitcherGalPerFilter;
    const botGal = c.bottledPerGal;

    let cmpDaily, cmpName, cmpGal;
    if (this.#source === 'bottled') {
      cmpDaily = botDaily;
      cmpName = 'bottled water';
      cmpGal = botGal;
    } else {
      cmpDaily = pitDaily;
      cmpName = 'a pitcher filter';
      cmpGal = pitGal;
    }

    const annualSavings = Math.max(0, Math.round((cmpDaily * 365) - c.borouxAnnual));
    let heroText, heroUnit;
    let maxScale;

    if (unit === 'day') {
      const sv = cmpDaily - bxDaily;
      heroText = sv > 0 ? ('$' + sv.toFixed(1)) : ('$' + bxDaily.toFixed(2));
      heroUnit = '/Day';

      this.refs.payTitle.textContent = 'Cost per day';
      maxScale = Math.max(bxDaily, pitDaily, botDaily);
      this.refs.payBoroux.textContent = '$' + bxDaily.toFixed(2);
      this.#setBar(this.refs.barBoroux, bxDaily, maxScale);
      this.refs.payPitcher.textContent = '$' + pitDaily.toFixed(2);
      this.#setBar(this.refs.barPitcher, pitDaily, maxScale);
      this.refs.payBottled.textContent = '$' + botDaily.toFixed(2);
      this.refs.barBottled.style.width = '100%';

      const mult = cmpDaily > 0 ? Math.round(cmpDaily / bxDaily) : 0;
      this.refs.payTakeaway.textContent = mult > 1
        ? (mult + 'x cheaper than ' + cmpName)
        : ('Clean water for just $' + bxDaily.toFixed(2) + '/day');

    } else if (unit === 'gallon') {
      const sv = cmpGal - bxGal;
      heroText = sv > 0 ? ('$' + sv.toFixed(2)) : ('$' + bxGal.toFixed(2));
      heroUnit = '/Gallon';

      this.refs.payTitle.textContent = 'Cost per gallon';
      maxScale = Math.max(bxGal, pitGal, botGal);
      this.refs.payBoroux.textContent = '$' + bxGal.toFixed(2);
      this.#setBar(this.refs.barBoroux, bxGal, maxScale);
      this.refs.payPitcher.textContent = '$' + pitGal.toFixed(2);
      this.#setBar(this.refs.barPitcher, pitGal, maxScale);
      this.refs.payBottled.textContent = '$' + botGal.toFixed(2);
      this.refs.barBottled.style.width = '100%';

      const mult = cmpGal > 0 ? Math.round(cmpGal / bxGal) : 0;
      this.refs.payTakeaway.textContent = mult > 1
        ? (mult + 'x cheaper per gallon than ' + cmpName)
        : ('Just $' + bxGal.toFixed(2) + ' per gallon');

    } else {
      const bxMo = c.borouxAnnual / 12;
      const pitMo = pitDaily * 30.4;
      const botMo = botDaily * 30.4;
      const cmpMo = cmpDaily * 30.4;
      const sv = cmpMo - bxMo;
      heroText = sv > 0 ? ('$' + Math.round(sv)) : ('$' + Math.round(bxMo));
      heroUnit = '/Month';

      this.refs.payTitle.textContent = 'Cost per month';
      maxScale = Math.max(bxMo, pitMo, botMo);
      this.refs.payBoroux.textContent = '$' + Math.round(bxMo);
      this.#setBar(this.refs.barBoroux, bxMo, maxScale);
      this.refs.payPitcher.textContent = '$' + Math.round(pitMo);
      this.#setBar(this.refs.barPitcher, pitMo, maxScale);
      this.refs.payBottled.textContent = '$' + Math.round(botMo);
      this.refs.barBottled.style.width = '100%';

      const mult = cmpMo > 0 ? Math.round(cmpMo / bxMo) : 0;
      this.refs.payTakeaway.textContent = mult > 1
        ? (mult + 'x cheaper per month than ' + cmpName)
        : ('Just $' + Math.round(bxMo) + '/month');
    }

    this.refs.heroAmount.textContent = heroText;
    this.refs.heroUnit.textContent = heroUnit;
    this.refs.heroAnnual.textContent = annualSavings > 0
      ? 'Save $' + annualSavings.toLocaleString() + '/Year'
      : '';

    // Contaminant takeaway
    this.refs.contaminantTakeaway.textContent = c.contaminantsRemoved + 'x more protection than a pitcher filter';

    // Impact strip
    const galOz = 128;
    const bottleOz = c.bottleOz;
    const bottlesYr = Math.round((annualGal * galOz) / bottleOz);
    const bottlesDay = Math.round((dailyGal * galOz) / bottleOz);

    if (this.#source === 'pitcher') {
      this.refs.impactBottles.textContent = Math.round(annualGal / c.pitcherGalPerFilter).toLocaleString();
      this.refs.impactBottlesLabel.textContent = 'filter replacements saved/yr';
    } else {
      this.refs.impactBottles.textContent = bottlesYr.toLocaleString();
      this.refs.impactBottlesLabel.textContent = 'plastic bottles eliminated/yr';
    }

    const monthsPayoff = annualSavings > 0 ? Math.ceil(c.systemCost / (annualSavings / 12)) : 0;
    if (monthsPayoff > 0 && monthsPayoff <= 24) {
      this.refs.impactPayoff.textContent = monthsPayoff;
      this.refs.impactPayoffLabel.textContent = monthsPayoff === 1
        ? 'month to pay off your system'
        : 'months to pay off your system';
    } else {
      this.refs.impactPayoff.textContent = '80+';
      this.refs.impactPayoffLabel.textContent = 'contaminants removed';
    }

    this.refs.impactDaily.textContent = bottlesDay;
    this.refs.impactDailyLabel.textContent = 'bottles of clean water daily';

    // Footer
    if (this.refs.savingsFooter) {
      this.refs.savingsFooter.textContent = annualSavings > 0
        ? 'You save $' + annualSavings.toLocaleString() + '/year vs. ' + cmpName
        : 'Clean, safe water for your whole family';
    }

    if (this.refs.footnote) {
      const fLabel = people <= 2 ? 'Family of 2' : people <= 4 ? 'Family of 4' : 'Family of 6';
      this.refs.footnote.textContent = fLabel + ', ~' + dailyGal + ' gal/day. BOROUX: $' + c.borouxAnnual + '/yr (12-mo replacement). Pitcher filter: $' + c.pitcherPerFilter + '/filter, ' + c.pitcherGalPerFilter + ' gal each. Bottled: ~$' + c.bottledPerGal.toFixed(2) + '/gal. ' + bottleOz + 'oz bottles. *PNAS, 2024. **EWG Tap Water Database.';
    }

    this.#animateBars();
  }

  #updateTapResults() {
    const c = this.#config;
    const people = this.#people;
    const unit = this.#unit;
    const dailyGal = people;
    const bxDaily = c.borouxAnnual / 365;
    const bxGal = c.borouxAnnual / (dailyGal * 365);
    const bxMonthly = c.borouxAnnual / 12;
    const bottlesDay = Math.round((dailyGal * 128) / c.bottleOz);

    let costText, costUnit;
    if (unit === 'day') {
      costText = '$' + bxDaily.toFixed(2);
      costUnit = '/Day';
    } else if (unit === 'gallon') {
      costText = '$' + bxGal.toFixed(2);
      costUnit = '/Gallon';
    } else {
      costText = '$' + Math.round(bxMonthly);
      costUnit = '/Month';
    }

    this.refs.heroAmount.textContent = costText;
    this.refs.heroUnit.textContent = costUnit;
    this.refs.heroAnnual.textContent = '$' + c.borouxAnnual + '/Year for your whole family';
    this.refs.tapBorouxPrice.textContent = costText;
    this.refs.tapBorouxUnit.textContent = costUnit;
    this.refs.tapDailyOutput.textContent = bottlesDay + ' bottles worth of clean water daily';
    this.refs.tapImpactDaily.textContent = bottlesDay;
    this.refs.tapImpactAnnual.textContent = '$' + c.borouxAnnual;
    this.refs.tapImpactAnnualLabel.textContent = 'per year for your whole family';

    const fLabel = people <= 2 ? 'Family of 2' : people <= 4 ? 'Family of 4' : 'Family of 6';
    if (this.refs.tapFooterMsg) {
      this.refs.tapFooterMsg.textContent = 'Clean, safe water for your whole family';
    }
    if (this.refs.tapFooterNote) {
      this.refs.tapFooterNote.textContent = fLabel + ', ~' + dailyGal + ' gal/day. BOROUX: $' + c.borouxAnnual + '/yr (12-mo replacement). *EWG Tap Water Database.';
    }
  }

  #setBar(el, value, max) {
    el.style.width = Math.max(2, Math.round((value / max) * 100)) + '%';
  }

  #animateBars() {
    const fills = this.refs.barResults.querySelectorAll('.wsc__fill');
    const widths = [];
    for (const fill of fills) {
      widths.push(fill.style.width);
      fill.style.transition = 'none';
      fill.style.width = '0%';
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        let delay = 0;
        for (let i = 0; i < fills.length; i++) {
          const fill = fills[i];
          fill.style.transition = 'width 1s var(--animation-easing, ease) ' + delay + 'ms';
          fill.style.width = widths[i];
          delay += 250;
        }
      });
    });
  }
}

if (!customElements.get('water-savings-calculator')) {
  customElements.define('water-savings-calculator', WaterSavingsCalculator);
}
